import { compareSnapshots } from '../diff';
import type {
  FireComplianceStatement,
  FireInspection,
  FireViolation,
  ParcelRecord,
  ParcelSnapshot,
  RecordKind,
  Section604Affidavit,
} from '../types';
import { materialChanges } from './changes';
import { canonicalFacts, materialHash } from './facts';
import { issueEventKey, issueIdentity, sourceEventIdentity } from './identity';
import { issuePriority } from './priority';
import { changeSignificance } from './significance';
import { classifySourceState } from './status';
import type {
  IssueEvent,
  IssueEvidence,
  IssueFacts,
  IssueProjection,
  IssueRawChange,
  IssueSource,
  ProjectedIssue,
  ProjectionContext,
  ProjectionInput,
} from './types';

const MAX_EVENTS = 10_000;
const MAX_FACT_BYTES = 2_000_000;

type SourceRecord = {
  source: IssueSource;
  kind: RecordKind;
  id: string;
  title: string;
  status?: string;
  closedAt?: string;
  sourceUrl: string;
  facts: IssueFacts;
};

export function projectIssues(
  previous: ProjectionInput,
  current: ParcelSnapshot,
  context: ProjectionContext,
): IssueProjection {
  validateContext(current, context);
  const previousRecords = indexRecords(previous, context.collectorScope);
  const currentRecords = indexRecords(current, context.collectorScope);
  const legacyMaterial = legacyMaterialKeys(previous, current, context.collectorScope);
  const currentSources = snapshotSources(current, context.collectorScope);
  const issues: ProjectedIssue[] = [];
  const events: IssueEvent[] = [];

  for (const record of currentRecords.values()) {
    const old = previousRecords.get(recordKey(record));
    const identity = identityFor(record, context);
    assertFactsBounded(record.facts);
    const hash = materialHash(record.facts);
    const prior = context.priorRevisions?.[identity];
    const rawChanges = old
      ? recordMaterialChanges(old, record, previous, current, legacyMaterial)
      : [];
    const materialCandidate = prior
      ? prior.materialHash !== hash
      : Boolean(old && materialHash(old.facts) !== hash);
    const changed = old
      ? materialCandidate && rawChanges.length > 0
      : Boolean(prior && materialCandidate);
    const sourceRevision = prior
      ? prior.sourceRevision + (changed ? 1 : 0)
      : old && changed
        ? 2
        : 1;
    const sourceState = classifySourceState({
      source: record.source,
      recordKind: record.kind,
      status: record.status,
      closedAt: record.closedAt,
    });
    const priority = issuePriority({
      facts: record.facts,
      source: record.source,
      recordKind: record.kind,
      sourceState,
      observedAt: context.observedAt,
    });
    issues.push({
      identity,
      source: record.source,
      recordKind: record.kind,
      sourceRecordId: record.id,
      title: record.title,
      facts: record.facts,
      materialHash: hash,
      sourceRevision,
      sourceState,
      sourceDueAt: priority.dueAt,
      policyVersion: context.policyVersion,
      priority: priority.priority,
      priorityReasons: priority.reasons,
      observedAt: context.observedAt,
      changedAt: changed ? context.observedAt : (prior?.changedAt ?? context.observedAt),
      evidence: evidence(record),
    });
    // A record the source already shows settled is history, and an edit that touches
    // only contact, administrative or identity details is not news: the projection
    // records both without an event, so nobody is told.
    const silent =
      historicalEvent(old ?? null, record) ||
      (old !== undefined &&
        !changeSignificance(old.facts, record.facts, {
          source: record.source,
          recordKind: record.kind,
        }).material);
    if (
      previous &&
      !old &&
      snapshotSources(previous, context.collectorScope).has(record.source) &&
      !scopeExpansionSuppresses(record, previous, current)
    ) {
      if (!silent)
        events.push(
          eventFor(record, null, record.facts, identity, sourceRevision, 'record_added', context),
        );
    } else if (changed && (old || prior) && !silent) {
      events.push(
        eventFor(
          record,
          old?.facts ?? null,
          record.facts,
          identity,
          sourceRevision,
          'record_changed',
          context,
          rawChanges,
        ),
      );
    }
  }

  if (previous) {
    for (const old of previousRecords.values()) {
      if (
        currentRecords.has(recordKey(old)) ||
        !currentSources.has(old.source) ||
        removalUpgradeSuppresses(old, previous, current, legacyMaterial)
      )
        continue;
      const identity = identityFor(old, context);
      const prior = context.priorRevisions?.[identity];
      const sourceRevision = (prior?.sourceRevision ?? 1) + 1;
      const facts: IssueFacts = { ...old.facts, sourceState: 'not_listed' };
      const priority = issuePriority({
        facts,
        source: old.source,
        recordKind: old.kind,
        sourceState: 'not_listed',
        observedAt: context.observedAt,
      });
      issues.push({
        identity,
        source: old.source,
        recordKind: old.kind,
        sourceRecordId: old.id,
        title: old.title,
        facts,
        materialHash: materialHash(facts),
        sourceRevision,
        sourceState: 'not_listed',
        sourceDueAt: priority.dueAt,
        policyVersion: context.policyVersion,
        priority: priority.priority,
        priorityReasons: priority.reasons,
        observedAt: context.observedAt,
        changedAt: context.observedAt,
        evidence: evidence(old),
      });
      // A record that was already closed and is no longer listed is not news either.
      if (!historicalEvent(old, null))
        events.push(
          eventFor(old, old.facts, facts, identity, sourceRevision, 'record_not_listed', context),
        );
    }
    events.push(...sourceEvents(previous, current, context));
    events.push(...upgradeEvents(previous, current, context));
  }

  if (events.length > MAX_EVENTS)
    throw new Error('Issue projection exceeds the event envelope limit.');
  return {
    schemaVersion: 1,
    projectionVersion: 1,
    collectorScope: context.collectorScope,
    observationRevision: context.observationRevision,
    issues: issues.sort((a, b) => a.identity.localeCompare(b.identity)),
    events: events.sort((a, b) => a.eventKey.localeCompare(b.eventKey)),
  };
}

function indexRecords(snapshot: ProjectionInput, scope: ProjectionContext['collectorScope']) {
  const records = new Map<string, SourceRecord>();
  if (!snapshot) return records;
  for (const record of sourceRecords(snapshot, scope)) {
    const key = recordKey(record);
    if (records.has(key)) throw new Error(`Duplicate official record identity: ${key}`);
    records.set(key, record);
  }
  return records;
}

function sourceRecords(snapshot: ParcelSnapshot, scope: ProjectionContext['collectorScope']) {
  if (scope === 'sf311') {
    return (snapshot.sf311?.cases ?? []).map((record) => ({
      source: 'sf311' as const,
      kind: '311' as const,
      id: requiredId(record.id, 'SF311'),
      title: title(record.details, record.category, record.id, '311'),
      status: record.status,
      closedAt: record.closedAt,
      sourceUrl: record.sourceUrl || snapshot.sf311!.sourceUrl,
      facts: canonicalFacts(record),
    }));
  }

  const result: SourceRecord[] = snapshot.records.map(parcelSourceRecord);
  if (snapshot.assessor) {
    result.push({
      source: 'assessor',
      kind: 'assessor',
      id: requiredId(snapshot.assessor.parcelNumber, 'assessor parcel'),
      title: `Assessor record ${snapshot.assessor.parcelNumber}`,
      sourceUrl: snapshot.assessor.sourceUrl,
      facts: canonicalFacts({
        parcelNumber: snapshot.assessor.parcelNumber,
        fields: snapshot.assessor.fields,
        history: snapshot.assessor.history ?? [],
        coverage: snapshot.assessor.coverage,
      }),
    });
  }
  for (const record of snapshot.recorder?.documents ?? []) {
    result.push({
      source: 'recorder',
      kind: 'recorder',
      id: requiredId(record.documentNumber || record.id, 'recorder'),
      title: title(record.titles[0], undefined, record.documentNumber || record.id, 'recorder'),
      sourceUrl: record.sourceUrl || snapshot.recorder!.sourceUrl,
      facts: canonicalFacts(record),
    });
  }
  for (const record of snapshot.subdivision?.projects ?? []) {
    result.push({
      source: 'subdivision',
      kind: 'subdivision',
      id: requiredId(record.id, 'subdivision'),
      title: title(record.name, record.type, record.id, 'subdivision'),
      status: record.status,
      sourceUrl: record.sourceUrl || snapshot.subdivision!.sourceUrl,
      facts: canonicalFacts(record),
    });
  }
  for (const record of snapshot.publicWorks?.records ?? []) {
    result.push({
      source: 'public-works',
      kind: 'public-works',
      id: requiredId(record.id, 'public-works'),
      title: truncateTitle(
        `${record.locationScope === 'address' ? 'Listed at this address' : 'On this block — not confirmed at this property'}: ${record.description}`,
      ),
      status: record.status,
      sourceUrl: record.sourceUrl,
      facts: canonicalFacts(record),
    });
  }
  result.push(
    ...safetyRecords(
      snapshot.safetyCompliance?.section604.affidavits ?? [],
      'section604',
      'section604',
    ),
    ...safetyRecords(
      snapshot.safetyCompliance?.fireInspections.inspections ?? [],
      'fire-inspections',
      'fire-inspection',
    ),
    ...safetyRecords(
      snapshot.safetyCompliance?.fireViolations.violations ?? [],
      'fire-violations',
      'fire-violation',
    ),
    ...safetyRecords(
      snapshot.safetyCompliance?.statements.statements ?? [],
      'fire-compliance',
      'fire-compliance',
    ),
  );
  return result;
}

function parcelSourceRecord(record: ParcelRecord): SourceRecord {
  return {
    source: 'dbi',
    kind: record.kind,
    id: requiredId(record.id, record.kind),
    title: title(record.description, undefined, record.id, record.kind),
    status: record.status,
    sourceUrl: record.sourceUrl,
    facts: canonicalFacts(record),
  };
}

function safetyRecords(
  records: Array<Section604Affidavit | FireInspection | FireViolation | FireComplianceStatement>,
  source: IssueSource,
  kind: RecordKind,
): SourceRecord[] {
  return records.map((record) => {
    const status = 'status' in record ? record.status : undefined;
    const closedAt =
      'closeDate' in record ? record.closeDate : 'endedAt' in record ? record.endedAt : undefined;
    const description = 'description' in record ? record.description : '';
    const secondary =
      'type' in record
        ? record.type
        : 'comments' in record
          ? record.comments
          : 'correctiveAction' in record
            ? record.correctiveAction
            : undefined;
    return {
      source,
      kind,
      id: requiredId(record.id, kind),
      title: title(description, secondary, record.id, kind),
      status,
      closedAt,
      sourceUrl: record.sourceUrl,
      facts: canonicalFacts(record),
    };
  });
}

const kindLabels: Record<RecordKind, string> = {
  'street-view': 'Street View capture',
  building: 'Building permit',
  electrical: 'Electrical permit',
  plumbing: 'Plumbing permit',
  complaint: 'Complaint',
  nov: 'Notice of violation',
  assessor: 'Assessor record',
  recorder: 'Recorded document',
  subdivision: 'Subdivision project',
  'public-works': 'Public Works permit',
  '311': '311 request',
  section604: 'Section 604 affidavit',
  'fire-inspection': 'Fire inspection',
  'fire-violation': 'Fire violation',
  'fire-compliance': 'Fire compliance statement',
  'tax-bill': 'Property tax bill',
};

function title(
  primary: string | undefined,
  secondary: string | undefined,
  officialId: string,
  kind: RecordKind,
) {
  for (const value of [primary, secondary]) {
    const normalized = value?.trim();
    if (
      normalized &&
      !/^(?:n\/?a|none|unknown|not available|recorded document|-+)$/i.test(normalized)
    )
      return truncateTitle(normalized);
  }
  return `${kindLabels[kind]} ${officialId}`;
}

function truncateTitle(value: string) {
  const characters = Array.from(value);
  return characters.length <= 1_000 ? value : `${characters.slice(0, 999).join('')}…`;
}

function requiredId(value: string, label: string) {
  const id = value.trim();
  if (!id) throw new Error(`${label} record has no official identity.`);
  return id;
}

function recordKey(record: Pick<SourceRecord, 'source' | 'kind' | 'id'>) {
  return JSON.stringify([record.source, record.kind, record.id]);
}

function identityFor(record: SourceRecord, context: ProjectionContext) {
  return issueIdentity({
    accountId: context.accountId,
    propertyId: context.propertyId,
    source: record.source,
    recordKind: record.kind,
    sourceRecordId: record.id,
  });
}

function evidence(record: SourceRecord): IssueEvidence {
  return {
    sourceUrl: record.sourceUrl,
    links: [{ label: 'Official record', url: record.sourceUrl }],
  };
}

/**
 * Closed or unlisted on both sides of the event: the city edited old history.
 * `null` is a side where the record is not listed. Each side is read from its own
 * record the way the projection states it, so a safety record closed by its
 * close date counts as settled before the edit as well as after.
 */
function historicalEvent(before: SourceRecord | null, after: SourceRecord | null) {
  const settled = (record: SourceRecord | null) =>
    record === null ||
    classifySourceState({
      source: record.source,
      recordKind: record.kind,
      status: record.status,
      closedAt: record.closedAt,
    }) === 'closed';
  return settled(before) && settled(after);
}

function eventFor(
  record: SourceRecord,
  before: IssueFacts | null,
  after: IssueFacts | null,
  identity: string,
  issueRevision: number,
  eventType: Extract<
    IssueEvent['eventType'],
    'record_added' | 'record_changed' | 'record_not_listed'
  >,
  context: ProjectionContext,
  rawChanges?: IssueRawChange[],
): IssueEvent {
  return {
    schemaVersion: 1,
    event_scope: 'record',
    source: record.source,
    recordKind: record.kind,
    sourceRecordId: record.id,
    eventKey: issueEventKey({
      identity,
      observationRevision: context.observationRevision,
      issueRevision,
      eventType,
    }),
    eventType,
    before,
    after,
    rawChanges: rawChanges ?? materialChanges(before, after),
    evidence: evidence(record),
    materialHash: materialHash(after ?? before ?? {}),
    parserVersion: context.parserVersion,
    policyVersion: context.policyVersion,
    observedAt: context.observedAt,
    issueIdentity: identity,
    issueRevision,
  };
}

function snapshotSources(snapshot: ParcelSnapshot, scope: ProjectionContext['collectorScope']) {
  if (scope === 'sf311') return new Set<IssueSource>(snapshot.sf311 ? ['sf311'] : []);
  const sources = new Set<IssueSource>(['dbi']);
  if (snapshot.assessor) sources.add('assessor');
  if (snapshot.recorder) sources.add('recorder');
  if (snapshot.subdivision) sources.add('subdivision');
  if (snapshot.publicWorks) sources.add('public-works');
  if (snapshot.safetyCompliance) {
    sources.add('section604');
    sources.add('fire-inspections');
    sources.add('fire-violations');
    sources.add('fire-compliance');
  }
  for (const coverage of snapshot.health.sources ?? []) {
    if (coverage.status === 'complete' && coverage.source !== 'sf311') sources.add(coverage.source);
  }
  return sources;
}

function sourceEvents(
  previous: ParcelSnapshot,
  current: ParcelSnapshot,
  context: ProjectionContext,
) {
  const before = snapshotSources(previous, context.collectorScope);
  const after = snapshotSources(current, context.collectorScope);
  const events: IssueEvent[] = [];
  for (const source of new Set([...before, ...after])) {
    if (before.has(source) === after.has(source)) continue;
    const identity = sourceEventIdentity({
      accountId: context.accountId,
      propertyId: context.propertyId,
      source,
    });
    const adopted = after.has(source);
    const eventType = adopted ? 'source_adopted' : 'source_lost';
    const facts = { available: adopted };
    events.push({
      schemaVersion: 1,
      event_scope: 'source',
      source,
      recordKind: null,
      sourceRecordId: null,
      eventKey: issueEventKey({
        identity,
        observationRevision: context.observationRevision,
        eventType,
      }),
      eventType,
      before: { available: !adopted },
      after: facts,
      rawChanges: materialChanges({ available: !adopted }, facts),
      evidence: {
        sourceUrl: sourceUrl(current, source) || sourceUrl(previous, source),
        links: [],
      },
      materialHash: materialHash(facts),
      parserVersion: context.parserVersion,
      policyVersion: context.policyVersion,
      observedAt: context.observedAt,
      issueIdentity: null,
      issueRevision: null,
    });
  }
  return events;
}

function legacyMaterialKeys(
  previous: ProjectionInput,
  current: ParcelSnapshot,
  scope: ProjectionContext['collectorScope'],
) {
  const keys = new Set<string>();
  if (!previous || scope !== 'full') return keys;
  const sf311 = current.sf311 ??
    previous.sf311 ?? {
      sourceUrl: current.sourceUrl,
      resultCount: 0,
      cases: [],
    };
  for (const change of compareSnapshots(previous, { ...current, sf311 })) {
    const source = sourceForKind(change.kind);
    if (!source) continue;
    const sourceRecordId =
      change.kind === 'assessor'
        ? (current.assessor?.parcelNumber ?? previous.assessor?.parcelNumber)
        : change.recordId;
    if (sourceRecordId) keys.add(JSON.stringify([source, change.kind, sourceRecordId]));
  }
  return keys;
}

function sourceForKind(kind: RecordKind): IssueSource | null {
  // 311 and tax bills keep independent baselines outside the full-scan projection.
  if (kind === '311' || kind === 'tax-bill' || kind === 'street-view') return null;
  if (
    kind === 'building' ||
    kind === 'electrical' ||
    kind === 'plumbing' ||
    kind === 'complaint' ||
    kind === 'nov'
  )
    return 'dbi';
  if (kind === 'fire-inspection') return 'fire-inspections';
  if (kind === 'fire-violation') return 'fire-violations';
  if (kind === 'fire-compliance') return 'fire-compliance';
  return kind;
}

function recordMaterialChanges(
  beforeRecord: SourceRecord,
  afterRecord: SourceRecord,
  previous: ProjectionInput,
  current: ParcelSnapshot,
  legacyMaterial: Set<string>,
) {
  const changes = materialChanges(beforeRecord.facts, afterRecord.facts);
  if (!previous) return changes;
  const before = previous.parserVersion ?? 1;
  const after = current.parserVersion ?? 1;
  const upgradeApplies =
    (afterRecord.kind === 'nov' && before < 2 && after >= 2) ||
    (afterRecord.kind === 'assessor' && before < 3 && after >= 3) ||
    (afterRecord.source === 'dbi' && before < 5 && after >= 5) ||
    (isSafetySource(afterRecord.source) && before < 8 && after >= 8) ||
    (afterRecord.source === 'fire-compliance' && before < 10 && after >= 10);
  if (upgradeApplies && !legacyMaterial.has(recordKey(afterRecord))) return [];
  if (isSafetySource(afterRecord.source) && before < 8 && after >= 8) return changes;

  return changes.filter((change) => {
    if (
      afterRecord.kind === 'nov' &&
      ((before < 2 && after >= 2) || (before < 5 && after >= 5)) &&
      change.path === 'status' &&
      change.before === 'Notice of Violation' &&
      typeof change.after === 'string' &&
      /^(?:OPEN|CLOSED)$/i.test(change.after)
    )
      return false;
    if (
      afterRecord.kind === 'assessor' &&
      before < 3 &&
      after >= 3 &&
      (change.path === 'history' || change.path.startsWith('history.'))
    )
      return false;
    return true;
  });
}

function scopeExpansionSuppresses(
  record: SourceRecord,
  previous: ParcelSnapshot,
  current: ParcelSnapshot,
) {
  return (
    isSafetySource(record.source) &&
    (previous.parserVersion ?? 1) < 9 &&
    (current.parserVersion ?? 1) >= 9 &&
    current.safetyCompliance?.scope?.type === 'condo-building'
  );
}

function removalUpgradeSuppresses(
  record: SourceRecord,
  previous: ParcelSnapshot,
  current: ParcelSnapshot,
  legacyMaterial: Set<string>,
) {
  return (
    !legacyMaterial.has(recordKey(record)) &&
    record.source === 'fire-compliance' &&
    (previous.parserVersion ?? 1) < 10 &&
    (current.parserVersion ?? 1) >= 10
  );
}

function isSafetySource(source: IssueSource) {
  return (
    source === 'section604' ||
    source === 'fire-inspections' ||
    source === 'fire-violations' ||
    source === 'fire-compliance'
  );
}

function upgradeEvents(
  previous: ParcelSnapshot,
  current: ParcelSnapshot,
  context: ProjectionContext,
) {
  if (context.collectorScope !== 'full') return [];
  const beforeVersion = previous.parserVersion ?? 1;
  const afterVersion = current.parserVersion ?? 1;
  const upgrades: Array<{
    version: number;
    type: Extract<IssueEvent['eventType'], 'source_parser_upgraded' | 'source_scope_expanded'>;
    sources: IssueSource[];
  }> = [
    { version: 2, type: 'source_parser_upgraded', sources: ['dbi'] },
    { version: 3, type: 'source_parser_upgraded', sources: ['assessor'] },
    { version: 5, type: 'source_parser_upgraded', sources: ['dbi'] },
    {
      version: 8,
      type: 'source_parser_upgraded',
      sources: ['section604', 'fire-inspections', 'fire-violations', 'fire-compliance'],
    },
    {
      version: 9,
      type: 'source_scope_expanded',
      sources:
        current.safetyCompliance?.scope?.type === 'condo-building'
          ? ['section604', 'fire-inspections', 'fire-violations', 'fire-compliance']
          : [],
    },
    {
      version: 10,
      type: 'source_parser_upgraded',
      sources: ['fire-compliance'],
    },
  ];
  return upgrades.flatMap(({ version, type, sources }) => {
    if (!(beforeVersion < version && afterVersion >= version)) return [];
    return sources.map((source) => sourceLifecycleEvent(source, type, version, context, current));
  });
}

function sourceLifecycleEvent(
  source: IssueSource,
  eventType: Extract<IssueEvent['eventType'], 'source_parser_upgraded' | 'source_scope_expanded'>,
  version: number,
  context: ProjectionContext,
  snapshot: ParcelSnapshot,
): IssueEvent {
  const identity = JSON.stringify([
    sourceEventIdentity({
      accountId: context.accountId,
      propertyId: context.propertyId,
      source,
    }),
    eventType,
    version,
  ]);
  const before = { version: version - 1 };
  const after = { version };
  return {
    schemaVersion: 1,
    event_scope: 'source',
    source,
    recordKind: null,
    sourceRecordId: null,
    eventKey: issueEventKey({
      identity,
      observationRevision: context.observationRevision,
      eventType,
    }),
    eventType,
    before,
    after,
    rawChanges: materialChanges(before, after),
    evidence: { sourceUrl: sourceUrl(snapshot, source), links: [] },
    materialHash: materialHash(after),
    parserVersion: context.parserVersion,
    policyVersion: context.policyVersion,
    observedAt: context.observedAt,
    issueIdentity: null,
    issueRevision: null,
  };
}

function sourceUrl(snapshot: ParcelSnapshot, source: IssueSource) {
  return (
    snapshot.health.sources?.find((entry) => entry.source === source)?.sourceUrl ??
    snapshot.sourceUrl
  );
}

function assertFactsBounded(facts: IssueFacts) {
  if (new TextEncoder().encode(JSON.stringify(facts)).length > MAX_FACT_BYTES)
    throw new Error('Issue facts exceed the projection envelope limit.');
}

function validateContext(current: ParcelSnapshot, context: ProjectionContext) {
  if (!context.accountId || !context.propertyId) throw new Error('Projection tenancy is required.');
  if (
    !/^\d{4}-\d{2}-\d{2}T/.test(context.observedAt) ||
    !Number.isFinite(Date.parse(context.observedAt))
  )
    throw new Error('Projection observedAt must be an ISO timestamp.');
  if (!Number.isSafeInteger(context.observationRevision) || context.observationRevision < 1)
    throw new Error('Projection observationRevision must be positive.');
  if (
    !Number.isSafeInteger(context.parserVersion) ||
    context.parserVersion < 1 ||
    !Number.isSafeInteger(context.policyVersion) ||
    context.policyVersion < 1
  )
    throw new Error('Projection versions must be positive.');
  if (context.collectorScope === 'full' && !current.health.complete)
    throw new Error('Full issue projection requires complete source coverage.');
  if (
    context.collectorScope === 'full' &&
    (current.assessor === undefined ||
      current.recorder === undefined ||
      current.subdivision === undefined ||
      current.safetyCompliance === undefined)
  )
    throw new Error('Full issue projection requires every owned source.');
  if (context.collectorScope === 'sf311' && current.sf311 === undefined)
    throw new Error('SF311 projection requires an SF311 snapshot.');
}

/** Source-local positive facts retain the established materiality and priority policy.
 * Coverage/removal inference stays in projectIssues at the full discovery barrier. */
export function sourceMaterialObservations(
  snapshot: ParcelSnapshot,
  scope: ProjectionContext['collectorScope'],
) {
  return sourceRecords(snapshot, scope).map((record) => {
    const facts = canonicalFacts(record.facts);
    if (scope === 'sf311') {
      for (const field of ['updatedAt', 'openedAt', 'closedAt']) delete facts[field];
      if (facts.fields && typeof facts.fields === 'object' && !Array.isArray(facts.fields)) {
        const fields = { ...facts.fields };
        for (const field of ['Updated', 'Opened', 'Closed']) delete fields[field];
        facts.fields = fields;
      }
    }
    const state = classifySourceState({
      source: record.source,
      recordKind: record.kind,
      status: record.status,
      closedAt: record.closedAt,
    });
    const priority = issuePriority({
      facts,
      source: record.source,
      recordKind: record.kind,
      sourceState: state,
      observedAt: snapshot.fetchedAt,
    });
    return {
      key: `${record.kind}:${record.id}`,
      sourceRecordId: record.id,
      source: record.source,
      category: record.kind,
      title: record.title,
      facts,
      priority: priority.priority,
      // The source already shows the record settled; later edits are history, not news.
      ...(state === 'closed' || state === 'not_listed' ? { historical: true } : {}),
      evidence: { sourceUrl: record.sourceUrl, links: [] },
    };
  });
}
